import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { Delete, Edit, RemoveRedEye, Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import DOMPurify from 'dompurify';
import { DataTable } from "../../libs/core/components/data-table";
import { useGetImpactsQuery, useDeleteImpactMutation } from "./api/impactsApi";
import { Impact } from "./types/impact";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import AddEditImpact from "./components/AddEditImpact";

const ImpactManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedImpact, setSelectedImpact] = useState<Impact | null>(null);
  const navigate = useNavigate();

  const { data: impacts, isLoading, isError, isFetching } = useGetImpactsQuery();
  const [deleteImpact] = useDeleteImpactMutation();

  const columns: Array<MRT_ColumnDef<Impact>> = [
    {
      header: "Image",
      accessorKey: "image_url",
      size: 80,
      Cell({ row: { original } }) {
        return original.image_url ? (
          <img
            src={original.image_url}
            alt={original.title}
            width={60}
            height={60}
            style={{ borderRadius: 6, objectFit: 'cover' }}
          />
        ) : (
          <Box
            width={60}
            height={60}
            sx={{
              backgroundColor: 'grey.200',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No Image
            </Typography>
          </Box>
        );
      },
    },
    {
      header: "Title",
      accessorKey: "title",
      size: 200,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {original.title || 'Untitled Impact'}
          </Typography>
        );
      },
    },
    {
      header: "Description",
      accessorKey: "description",
      size: 300,
      Cell({ row: { original } }) {
        const description = original.description || "";
        const sanitizedDescription = DOMPurify.sanitize(description);
        return (
          <Box 
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 300,
              '& ul': { 
                paddingLeft: 2,
                margin: 0,
                '& li': {
                  marginBottom: 0.5
                }
              }
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizedDescription.substring(0, 100) + '...'
            }}
          />
        );
      },
    },
    {
      header: "Value",
      accessorKey: "value",
      size: 150,
      Cell({ row: { original } }) {
        return (
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {original.value || 'Not specified'}
          </Typography>
        );
      },
    },
    {
      header: "Created At",
      accessorKey: "created_at",
      size: 120,
      Cell({ row: { original } }) {
        return new Date(original.created_at).toLocaleDateString();
      },
    },
    {
      header: "Actions",
      id: "actions",
      size: 120,
      Cell({ row: { original } }) {
        return (
          <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/impacts/${original.id}`);
              }}
              color="primary"
            >
              <RemoveRedEye />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImpact(original);
                setOpenDialog(true);
              }}
              color="primary"
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImpact(original);
                setOpenConfirmModal(true);
              }}
              color="error"
            >
              <Delete />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleDelete = async () => {
    if (selectedImpact) {
      try {
        await deleteImpact(selectedImpact.id.toString()).unwrap();
        setOpenConfirmModal(false);
        setSelectedImpact(null);
      } catch (error) {
        console.error("Failed to delete impact:", error);
      }
    }
  };

  const handleSuccess = () => {
    setSelectedImpact(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Impact Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setSelectedImpact(null);
            setOpenDialog(true);
          }}
        >
          Add New Impact
        </Button>
      </Box>

      <DataTable
        columns={columns}
        list={impacts?.data || []}
        count={impacts?.data?.length || 0}
        getRowId={(row) => row?.id?.toString() || Math.random().toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        pagination={pagination}
        onPaginationChange={setPagination}
        enableRowSelection={false}
        onRowClick={(row) => navigate(`/impacts/${row.id}`)}
      />

      <AddEditImpact
        open={openDialog}
        setModalOpen={setOpenDialog}
        onSuccess={handleSuccess}
        impact={selectedImpact}
      />

      <ConfirmModal
        modalOpen={openConfirmModal}
        setModalOpen={setOpenConfirmModal}
        onOk={handleDelete}
        title="Delete Impact"
        message={`Are you sure you want to delete "${selectedImpact?.title || 'this impact'}"? This action cannot be undone.`}
        type="warning"
      />
    </Box>
  );
};

export default ImpactManager;
